import PhoneIcon from '@mui/icons-material/Phone';
import { InputAdornment, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import PhoneInput, { formatPhoneNumberIntl, isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

/**
 * Custom input component that integrates react-phone-number-input with MUI TextField
 */
const CustomInput = forwardRef(
  ({ value, onChange, onBlur, disabled, error, helperText, label, required, ...rest }, ref) => (
    <TextField
      {...rest}
      inputRef={ref}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      onBlur={onBlur}
      disabled={disabled}
      error={error}
      helperText={helperText}
      label={label}
      required={required}
      fullWidth
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <PhoneIcon sx={{ color: 'action.active' }} />
          </InputAdornment>
        ),
      }}
    />
  ),
);

CustomInput.displayName = 'CustomInput';

CustomInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
};

/**
 * InternationalPhoneField - A phone input component with international support
 *
 * Features:
 * - Country selector with flags
 * - Auto-formatting based on selected country
 * - Stores in E.164 format (+14155551234)
 * - Built-in validation
 * - Works with Formik
 * - Default country: US
 *
 * @param {Object} props
 * @param {string} props.value - Phone number value (E.164 format or raw digits)
 * @param {function} props.onChange - Called with E.164 formatted value
 * @param {function} props.onBlur - Called on blur
 * @param {string} props.label - Field label
 * @param {string} props.defaultCountry - Default country code (default: 'US')
 * @param {boolean} props.disabled - Disable the input
 * @param {boolean} props.required - Show required indicator
 * @param {boolean} props.error - Show error state
 * @param {string} props.helperText - Helper/error text
 * @param {boolean} props.showValidation - Show validation error for invalid numbers
 */
const InternationalPhoneField = ({
  value,
  onChange,
  onBlur,
  label = 'Phone',
  defaultCountry = 'US',
  disabled = false,
  required = false,
  error = false,
  helperText = '',
  showValidation = true,
  name,
  ...rest
}) => {
  // Normalize incoming value - handle legacy raw digits format
  const normalizeValue = rawValue => {
    if (!rawValue) return '';

    // If it already starts with +, it's E.164 format
    if (rawValue.startsWith('+')) {
      return rawValue;
    }

    // If it's 10 digits, assume US number and convert to E.164
    const digits = rawValue.replace(/\D/g, '');
    if (digits.length === 10) {
      return `+1${digits}`;
    }

    // If it's 11 digits starting with 1, assume US with country code
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    }

    // Otherwise return as-is (might be partial input)
    return rawValue;
  };

  const normalizedValue = normalizeValue(value);

  // Determine validation error
  const isInvalid = showValidation && normalizedValue && !isValidPhoneNumber(normalizedValue);
  const displayError = error || isInvalid;
  const displayHelperText = isInvalid ? 'Invalid phone number' : helperText;

  const handleChange = newValue => {
    // Pass E.164 formatted value (or empty string) to parent
    onChange(newValue || '');
  };

  return (
    <div className="international-phone-field">
      <PhoneInput
        international
        countryCallingCodeEditable={false}
        defaultCountry={defaultCountry}
        value={normalizedValue}
        onChange={handleChange}
        onBlur={onBlur}
        disabled={disabled}
        inputComponent={CustomInput}
        numberInputProps={{
          label,
          required,
          error: displayError,
          helperText: displayHelperText,
          name,
          ...rest,
        }}
      />
      <style>{`
        .international-phone-field .PhoneInput {
          display: flex;
          align-items: center;
        }
        .international-phone-field .PhoneInputCountry {
          margin-right: 8px;
          padding-bottom: 4px;
        }
        .international-phone-field .PhoneInputCountrySelect {
          padding: 8px;
          border: 1px solid var(--input-border);
          border-radius: var(--button-radius);
          background: var(--input-bg);
          cursor: pointer;
          height: 40px;
        }
        .international-phone-field .PhoneInputCountrySelect:hover {
          border-color: var(--input-border-hover);
        }
        .international-phone-field .PhoneInputCountrySelect:focus {
          border-color: var(--input-focus-border);
          outline: none;
        }
        .international-phone-field .PhoneInputInput {
          flex: 1;
        }
      `}</style>
    </div>
  );
};

InternationalPhoneField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  label: PropTypes.string,
  defaultCountry: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  showValidation: PropTypes.bool,
  name: PropTypes.string,
};

export default InternationalPhoneField;

// Re-export useful utilities from the library
export { formatPhoneNumberIntl, isValidPhoneNumber, parsePhoneNumber };
